import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import {
  applyTelephonyPackageToggle,
  filterSearchViewAllowedObjects,
  getPackageRequiredObjectNames,
  getStandardTabPackageKey,
  isStandardObjectPackageRequired,
  isStandardTabPackageRequired,
  isAlwaysMigrateObject,
  mergeAlwaysIncludedAllowedObjects,
  normalizePlanConfig,
  removeAllowedObjectsForPackage,
  STANDARD_OBJECT_OPTIONS,
  REAL_ESTATE_PACKAGE_OBJECT_NAMES,
  VISIT_MANAGER_PACKAGE_OBJECT_NAMES,
} from './AdminPlanConfigConstants';
import PlanConfigForm from './PlanConfigForm';
import { isAdminModuleFullPlanEditor } from '../utils/adminSession';
import '../styles/admin-theme.css';

/** Deep merge: base + overrides (overrides win). */
function deepMerge(base, overrides) {
  if (!overrides || typeof overrides !== 'object') return base ? JSON.parse(JSON.stringify(base)) : {};
  const out = base ? JSON.parse(JSON.stringify(base)) : {};
  Object.keys(overrides).forEach((k) => {
    if (overrides[k] != null && typeof overrides[k] === 'object' && !Array.isArray(overrides[k])) {
      out[k] = deepMerge(out[k] || {}, overrides[k]);
    } else {
      out[k] = overrides[k];
    }
  });
  return out;
}

const SubscriptionConfigModal = ({ show, onHide, companyId, subscription, onSuccess, tenantCode }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [planConfig, setPlanConfig] = useState(null);
  const [config, setConfig] = useState({});
  const [standardObjects, setStandardObjects] = useState([]);
  const [standardTabs, setStandardTabs] = useState([]);
  const [tenantObjectConfigRows, setTenantObjectConfigRows] = useState([]);

  useEffect(() => {
    if (!show || !subscription?.plan_id) return;
    setError('');
    setLoading(true);
    adminSchemaApi
      .fetchPlanById(subscription.plan_id)
      .then(async (plan) => {
        const base = normalizePlanConfig(plan?.config || {});
        setPlanConfig(base);
        const overrides = subscription.config && typeof subscription.config === 'object' ? subscription.config : {};
        let merged = normalizePlanConfig(deepMerge(base, overrides));
        if (!isAdminModuleFullPlanEditor()) {
          merged = { ...merged, setup: { ...merged.setup, showSetup: false } };
        }
        setConfig(merged);
        const packages = overrides.enabledPackages ?? base.enabledPackages ?? [];
        const [objects, tabs] = await Promise.all([
          adminSchemaApi.fetchStandardObjects(packages).catch(() => []),
          adminSchemaApi.fetchStandardTabs(packages).catch(() => []),
        ]);
        setStandardObjects(Array.isArray(objects) ? objects : []);
        setStandardTabs(Array.isArray(tabs) ? tabs : []);
      })
      .catch(() => setError('Failed to load plan config'))
      .finally(() => setLoading(false));
  }, [show, subscription?.plan_id, subscription?.config]);

  useEffect(() => {
    if (!show || !tenantCode || !String(tenantCode).trim()) {
      console.info('[SearchViews:SubscriptionConfig] skip tenant object_config fetch', {
        show,
        tenantCodePresent: Boolean(tenantCode && String(tenantCode).trim()),
      });
      setTenantObjectConfigRows([]);
      return;
    }
    let cancelled = false;
    const tc = String(tenantCode).trim();
    adminSchemaApi
      .fetchObjects(tc)
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        const customNames = list.filter((r) => r?.is_standard === false).map((r) => r.object_name);
        console.info('[SearchViews:SubscriptionConfig] tenant object_config loaded', {
          tenantCode: tc,
          rowCount: list.length,
          customObjectNames: customNames,
        });
        if (!cancelled) setTenantObjectConfigRows(list);
      })
      .catch((err) => {
        console.warn('[SearchViews:SubscriptionConfig] tenant object_config fetch failed', { tenantCode: tc, err });
        if (!cancelled) setTenantObjectConfigRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [show, tenantCode]);

  const handlePackageToggle = useCallback(
    async (packageKey, checked) => {
      const next = (() => {
        let n = applyTelephonyPackageToggle(config.enabledPackages ?? [], packageKey, checked);
        if (checked && packageKey === 'email_campaign') {
          n = [...new Set([...n, 'watconnect'])];
        }
        return n;
      })();

      const tabsBefore = standardTabs;
      const objectsBefore = standardObjects;
      const pkLc = String(packageKey || '').toLowerCase().trim();

      const [objects, tabs] = await Promise.all([
        adminSchemaApi.fetchStandardObjects(next).catch(() => []),
        adminSchemaApi.fetchStandardTabs(next).catch(() => []),
      ]);
      const objs = Array.isArray(objects) ? objects : [];
      const tbs = Array.isArray(tabs) ? tabs : [];
      setStandardObjects(objs);
      setStandardTabs(tbs);

      if (checked) {
        const packageTabIdsToAdd = tbs
          .filter((t) => getStandardTabPackageKey(t) === pkLc)
          .filter((t) => isStandardTabPackageRequired(t))
          .map((t) => t.id)
          .filter(Boolean);
        const namesFromApi = objs
          .filter((o) => String(o?.config?.package ?? '').toLowerCase().trim() === pkLc)
          .filter((o) => isStandardObjectPackageRequired(o))
          .map((o) => o.object_name)
          .filter(Boolean);
        const extraEmail =
          packageKey === 'email_campaign' ? ['email_campaign', 'email_campaign_member'] : [];
        const extraVisitManager =
          packageKey === 'visit_manager' ? [...VISIT_MANAGER_PACKAGE_OBJECT_NAMES] : [];
        const extraRealEstate =
          packageKey === 'real_estate' ? [...REAL_ESTATE_PACKAGE_OBJECT_NAMES] : [];
        const namesToAdd = [...new Set([...namesFromApi, ...extraEmail, ...extraVisitManager, ...extraRealEstate])];

        setConfig((prev) => ({
          ...prev,
          enabledPackages: next,
          allowedObjectsPermission: mergeAlwaysIncludedAllowedObjects(
            [...new Set([...(prev.allowedObjectsPermission ?? []), ...namesToAdd])]
          ),
          objectManager: {
            ...prev.objectManager,
            allowedObjects: mergeAlwaysIncludedAllowedObjects(
              [...new Set([...(prev.objectManager?.allowedObjects ?? []), ...namesToAdd])]
            ),
          },
          ...(packageTabIdsToAdd.length > 0
            ? { tab: { ...prev.tab, allowedTabs: [...new Set([...(prev.tab?.allowedTabs ?? []), ...packageTabIdsToAdd])] } }
            : {}),
        }));
      } else {
        const packageTabIdsToRemove = new Set(
          (tabsBefore || [])
            .filter((t) => getStandardTabPackageKey(t) === pkLc)
            .map((t) => t.id)
            .filter(Boolean)
        );
        setConfig((prev) => {
          let allowedPermission = removeAllowedObjectsForPackage(
            prev.allowedObjectsPermission ?? [],
            objectsBefore,
            packageKey
          );
          let allowedOm = removeAllowedObjectsForPackage(
            prev.objectManager?.allowedObjects ?? [],
            objectsBefore,
            packageKey
          );
          if (packageKey === 'email_campaign') {
            allowedPermission = allowedPermission.filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member');
            allowedOm = allowedOm.filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member');
          }
          if (packageKey === 'visit_manager') {
            const rm = new Set(VISIT_MANAGER_PACKAGE_OBJECT_NAMES);
            allowedPermission = allowedPermission.filter((o) => !rm.has(o));
            allowedOm = allowedOm.filter((o) => !rm.has(o));
          }
          if (packageKey === 'real_estate') {
            const rm = new Set(REAL_ESTATE_PACKAGE_OBJECT_NAMES);
            allowedPermission = allowedPermission.filter((o) => !rm.has(o));
            allowedOm = allowedOm.filter((o) => !rm.has(o));
          }
          const allowedTabs = (prev.tab?.allowedTabs ?? []).filter((id) => !packageTabIdsToRemove.has(id));
          return {
            ...prev,
            enabledPackages: next,
            tab: { ...prev.tab, allowedTabs },
            allowedObjectsPermission: mergeAlwaysIncludedAllowedObjects(allowedPermission),
            objectManager: {
              ...prev.objectManager,
              allowedObjects: mergeAlwaysIncludedAllowedObjects(allowedOm),
            },
          };
        });
      }
    },
    [config.enabledPackages, standardObjects, standardTabs]
  );

  const standardPicklistOptions = useMemo(() => {
    let options;
    if (standardObjects.length > 0) {
      options = standardObjects.map((o) => {
        const name = o.object_name || '';
        const fromConfig = o.config?.display_label || o.config?.label;
        const label =
          (fromConfig && String(fromConfig).trim()) ||
          name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return { key: name, value: label || name };
      });
    } else {
      options = STANDARD_OBJECT_OPTIONS.map((o) => ({ key: o.name, value: o.label }));
    }
    return options.filter((o) => !isAlwaysMigrateObject(o.key));
  }, [standardObjects]);

  const leadConversionPicklistOptions = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return standardPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [standardPicklistOptions, config.allowedObjectsPermission]);

  useEffect(() => {
    if (!show || !standardObjects.length) return;
    const req = getPackageRequiredObjectNames(standardObjects, config.enabledPackages ?? []);
    if (!req.length) return;
    const cur = config.allowedObjectsPermission ?? [];
    const missing = req.filter((n) => !cur.includes(n));
    if (!missing.length) return;
    setConfig((prev) => ({
      ...prev,
      allowedObjectsPermission: mergeAlwaysIncludedAllowedObjects(
        [...new Set([...(prev.allowedObjectsPermission ?? []), ...missing])]
      ),
      objectManager: {
        ...prev.objectManager,
        allowedObjects: mergeAlwaysIncludedAllowedObjects(
          [...new Set([...(prev.objectManager?.allowedObjects ?? []), ...missing])]
        ),
      },
    }));
  }, [show, standardObjects, config.enabledPackages, config.allowedObjectsPermission]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyId || !subscription?.id) return;
    setSaving(true);
    setError('');
    try {
      const req = getPackageRequiredObjectNames(standardObjects, config.enabledPackages ?? []);
      const perm = mergeAlwaysIncludedAllowedObjects(
        [...new Set([...req, ...(config.allowedObjectsPermission ?? [])])]
      );
      const om = mergeAlwaysIncludedAllowedObjects(
        [...new Set([...req, ...(config.objectManager?.allowedObjects ?? [])])]
      );
      const toSave = {
        ...config,
        setup: {
          ...config.setup,
          ...(isAdminModuleFullPlanEditor() ? {} : { showSetup: false }),
        },
        allowedObjectsPermission: perm,
        objectManager: {
          ...config.objectManager,
          allowedObjects: om,
        },
        searchView: config.searchView
          ? {
              ...config.searchView,
              allowedObjects: filterSearchViewAllowedObjects(config.searchView.allowedObjects ?? []),
            }
          : config.searchView,
      };
      await adminSchemaApi.updateSubscription(companyId, subscription.id, { config: toSave });
      onHide();
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (!subscription) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="admin-title">
          <FaCog className="me-2" />
          Override config — {subscription.name || subscription.id}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <p className="text-muted small mb-3">
            Override plan config for this subscription. Same structure as plan config. Values here replace the plan defaults.
          </p>
          {loading ? (
            <p className="admin-subtitle">Loading…</p>
          ) : planConfig ? (
            <PlanConfigForm
              config={config}
              onChange={setConfig}
              disabled={saving}
              standardPicklistOptions={standardPicklistOptions}
              leadConversionPicklistOptions={leadConversionPicklistOptions}
              onPackageToggle={handlePackageToggle}
              standardTabs={standardTabs}
              standardObjects={standardObjects}
              tenantObjectConfigRows={tenantObjectConfigRows}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button className="admin-primary-btn" type="submit" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubscriptionConfigModal;
